<script setup lang="ts">
// Branded centered confirm/alert/prompt dialog with a blurred backdrop.
//
// Mounted exactly once at the app root (App.vue) — every call to
// `useConfirm().confirm(...)`, `.alert(...)` or `.prompt(...)` mutates
// the shared singleton state in `confirm.ts`, so we never have to add
// a <ConfirmDialog/> per view.
//
// A11y: traps focus on the primary control when shown, restores focus
// on close, supports Esc to cancel and Enter to confirm. The destructive
// variant turns the confirm button red so dangerous operations stand
// apart visually.

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, Info, ShieldAlert } from 'lucide-vue-next'
import { confirmState, resolveConfirm } from '@/lib/confirm'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'

const { t } = useI18n()

const open = computed(() => confirmState.value !== null)
const opts = computed(() => confirmState.value)

const confirmBtn = ref<HTMLButtonElement | null>(null)
const inputEl = ref<InstanceType<typeof Input> | null>(null)
const inputValue = ref<string>('')
const inputTouched = ref(false)

watch(open, async (v) => {
  if (v && opts.value) {
    inputValue.value = opts.value.defaultValue ?? ''
    inputTouched.value = false
    await nextTick()
    if (opts.value.mode === 'prompt') {
      const el = (inputEl.value as unknown as { $el?: HTMLInputElement } | null)?.$el
        ?? document.querySelector<HTMLInputElement>('[data-confirm-input]')
      el?.focus()
      el?.select?.()
    } else {
      // confirmBtn wraps the <Button> component, so .value is the instance,
      // not the DOM node — reach its root el (Button forwards $el).
      const btn = (confirmBtn.value as unknown as { $el?: HTMLElement } | null)?.$el
      btn?.focus?.()
    }
  }
})

const inputInvalid = computed(() => {
  if (!opts.value || opts.value.mode !== 'prompt') return false
  if (!opts.value.required) return false
  return inputValue.value.trim().length === 0
})

function onConfirm() {
  if (!opts.value) return
  if (opts.value.mode === 'prompt') {
    inputTouched.value = true
    if (inputInvalid.value) return
    resolveConfirm(inputValue.value)
  } else {
    resolveConfirm(true)
  }
}

function onCancel() {
  if (!opts.value) return
  resolveConfirm(opts.value.mode === 'prompt' ? null : false)
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    onCancel()
  } else if (e.key === 'Enter') {
    // For prompts, only confirm if Enter is in the input (avoid stealing
    // Enter from textarea-like surroundings).
    e.preventDefault()
    onConfirm()
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

const variantIcon = computed(() => {
  switch (opts.value?.variant) {
    case 'destructive': return AlertTriangle
    case 'warning': return ShieldAlert
    default: return Info
  }
})
const variantTone = computed(() => {
  switch (opts.value?.variant) {
    case 'destructive': return 'text-destructive bg-destructive/10'
    case 'warning': return 'text-status-warning bg-status-warning-bg'
    default: return 'text-primary bg-primary/10'
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="confirm-fade">
      <div
        v-if="open && opts"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`confirm-title-${opts.id}`"
      >
        <!-- Backdrop with blur -->
        <div
          class="absolute inset-0 bg-foreground/30"
          @click="opts.dismissOnBackdrop !== false && onCancel()"
        />
        <!-- Card -->
        <div
          class="relative w-full max-w-md rounded-card bg-card text-card-foreground shadow-[0_24px_64px_-16px_rgb(18_20_23/0.3)] p-6 space-y-5 animate-in"
        >
          <div class="flex items-start gap-4">
            <div
              class="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
              :class="variantTone"
            >
              <component
                :is="variantIcon"
                class="h-5 w-5"
              />
            </div>
            <div class="flex-1 min-w-0 space-y-1.5">
              <h2
                :id="`confirm-title-${opts.id}`"
                class="text-base font-semibold leading-tight"
              >
                {{ opts.title }}
              </h2>
              <p
                v-if="opts.message"
                class="text-sm text-muted-foreground leading-relaxed whitespace-pre-line"
              >
                {{ opts.message }}
              </p>
            </div>
          </div>

          <div
            v-if="opts.mode === 'prompt'"
            class="pl-14"
          >
            <Input
              ref="inputEl"
              v-model="inputValue"
              :type="opts.inputType ?? 'text'"
              :placeholder="opts.placeholder ?? ''"
              :error="inputTouched && inputInvalid"
              data-confirm-input
            />
            <p
              v-if="inputTouched && inputInvalid"
              class="text-xs text-destructive mt-1.5"
            >
              {{ t('common.field_required') }}
            </p>
          </div>

          <div class="flex items-center justify-end gap-2 pt-1">
            <Button
              v-if="opts.mode !== 'alert'"
              variant="ghost"
              size="sm"
              @click="onCancel"
            >
              {{ opts.cancelLabel }}
            </Button>
            <Button
              ref="confirmBtn"
              size="sm"
              :variant="opts.variant === 'destructive' ? 'destructive' : 'default'"
              @click="onConfirm"
            >
              {{ opts.confirmLabel }}
            </Button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 150ms ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
.animate-in {
  animation: pop 160ms ease-out;
}
@keyframes pop {
  from { transform: translateY(8px) scale(0.97); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
</style>
